/** Measurement property type */
export enum MeasurementOnSitePropertyType {
  APARTMENT_SINGLE_FLOORS = 'APARTMENT_SINGLE_FLOORS',
  SINGLE_FAMILY_HOUSE = 'SINGLE_FAMILY_HOUSE',
  SEMI_DETACHED_HOUSE = 'SEMI_DETACHED_HOUSE',
  APARTMENT_BUILDING = 'APARTMENT_BUILDING',
}

/**
 * Cellar scope/presence of the On-Site Area Report (mirrors BE CellarScope, ISSUE-2458/2459).
 * ROOM is measured but free; only WHOLE is charged; PRESENT_NOT_MEASURED is captured for the
 * invoice (Iisy) and the on-site technician without being measured.
 */
export enum CellarScope {
  NONE = 'NONE',
  PRESENT_NOT_MEASURED = 'PRESENT_NOT_MEASURED',
  ROOM = 'ROOM',
  WHOLE = 'WHOLE',
}

/** Attic scope/presence of the On-Site Area Report (mirrors BE AtticScope, ISSUE-2458/2459) */
export enum AtticScope {
  NONE = 'NONE',
  PRESENT_NOT_MEASURED = 'PRESENT_NOT_MEASURED',
  MEASURED = 'MEASURED',
}

/** Whether the cellar scope includes actual measurement (mirrors BE CellarScope.measured) */
export const isCellarScopeMeasured = (scope: CellarScope): boolean => scope === CellarScope.ROOM || scope === CellarScope.WHOLE

/** Whether the attic scope includes actual measurement (mirrors BE AtticScope.measured) */
export const isAtticScopeMeasured = (scope: AtticScope): boolean => scope === AtticScope.MEASURED

export enum MeasurementOnSiteAdditionalInformationType {
  DOCUMENTS_FOR_THE_BANK = 'DOCUMENTS_FOR_THE_BANK',
  RENTAL_DISPUTE = 'RENTAL_DISPUTE',
  BUY_OR_SELL = 'BUY_OR_SELL',
  PROPERTY_TAX_RETURN = 'PROPERTY_TAX_RETURN',
  OTHER = 'OTHER',
}

/** Measurement on site request DTO */
export interface MeasurementOnSiteRequestDTO {
  /** Property type for measurement on site */
  propertyType: MeasurementOnSitePropertyType,
  /** Surface area of the property */
  surfaceArea: number,
  /** Number of rooms in the property — no longer sent by the purchase flow (ISSUE-2188 cutover) */
  numberOfRooms?: number,
  /** Number of floors in the property */
  numberOfFloors: number,
  /** Number of residential units */
  numberOfResidentialUnits?: number,
  /** Additional area for basement — deprecated on the BE in favour of cellarScope; must equal the scope's measured-ness when the scope is sent */
  basementAreaToMeasure: boolean,
  /** Additional area for attic — deprecated on the BE in favour of atticScope; must equal the scope's measured-ness when the scope is sent */
  atticAreaToMeasure: boolean,
  /** Cellar scope/presence (ISSUE-2459) — sent by the On-Site Area Report redesign; when omitted the BE derives it from basementAreaToMeasure */
  cellarScope?: CellarScope,
  /** Attic scope/presence (ISSUE-2459) — sent by the On-Site Area Report redesign; when omitted the BE derives it from atticAreaToMeasure */
  atticScope?: AtticScope,
  /** Express Service add-on (ISSUE-2838) — appointment within 5 business days; priced off the quote, not as a cart product */
  expressService?: boolean,
  /** Additional House Views add-on (ISSUE-2838) — 4 extra viewpoints (front, back, left, right) */
  additionalHouseViews?: boolean,
  /** Additional information */
  additionalInformation?: MeasurementOnSiteAdditionalInformationType[],
  /** Comment */
  comment?: string,
}
